import React from 'react';
import './ImpactSurveyAnalytics.css';

function ratingTextClass(rating) {
  if (rating >= 4) return 'badge-positive-text';
  if (rating >= 3) return 'badge-pending-text';
  if (rating > 0) return 'badge-negative-text';
  return '';
}

function CountBars({ entries, total }) {
  if (!entries || entries.length === 0) {
    return <p style={{ fontSize: '0.85rem', color: '#718096' }}>No data yet.</p>;
  }
  const max = Math.max(...entries.map((e) => e.count), 1);
  return (
    <div>
      {entries.map((e) => {
        const pct = total > 0 ? Math.round((e.count / total) * 100) : 0;
        const width = Math.round((e.count / max) * 100);
        return (
          <div key={e.name} className="impact-bar-row">
            <span>{e.name}</span>
            <div className="impact-bar-track">
              <div className="impact-bar-fill" style={{ width: `${width}%` }} />
            </div>
            <span className="impact-bar-value">{e.count} ({pct}%)</span>
          </div>
        );
      })}
    </div>
  );
}

function RatingBars({ ratings, questions }) {
  const ratingQuestions = questions.filter((q) => q.type === 'rating');
  return (
    <div>
      {ratingQuestions.map((q) => {
        const value = ratings?.[q.key] ?? 0;
        const pct = (value / (q.max || 5)) * 100;
        return (
          <div key={q.key} className="impact-bar-row">
            <span>{q.label}</span>
            <div className="impact-bar-track">
              <div
                className="impact-bar-fill impact-bar-rating-fill"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="impact-bar-value">
              {value > 0 ? value.toFixed(2) : '\u2014'} / {q.max}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function breakdownToEntries(breakdown) {
  if (!breakdown) return [];
  return Object.entries(breakdown)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

export default function ImpactSurveyAnalytics({ analytics }) {
  if (!analytics) {
    return <div className="loading">Loading IMPACT survey analytics...</div>;
  }

  const {
    total,
    avg_overall_experience,
    avg_likelihood_to_attend_2026,
    role_breakdown,
    age_group_breakdown,
    met_expectations_breakdown,
    outcome_breakdown,
    avg_ratings,
    top_sessions,
    how_heard_breakdown,
    survey_type_breakdown,
    survey_type,
    questions = [],
  } = analytics;

  const attendeeCount = (survey_type_breakdown || {}).attendee || 0;
  const vlCount = (survey_type_breakdown || {}).volunteer_leader || 0;
  const corpusTotal = attendeeCount + vlCount;

  const roles = breakdownToEntries(role_breakdown);
  const ages = breakdownToEntries(age_group_breakdown);
  const expectations = breakdownToEntries(met_expectations_breakdown);

  const heading = survey_type === 'attendee'
    ? 'IMPACT 2025 Survey Overview — Attendees'
    : survey_type === 'volunteer_leader'
      ? 'IMPACT 2025 Survey Overview — Volunteers / Leaders'
      : 'IMPACT 2025 Survey Overview';

  return (
    <div className="card impact-survey-analytics">
      <h3>{heading}</h3>

      {corpusTotal > 0 && (
        <div className="impact-breakdown-badges" style={{ marginBottom: '0.75rem' }}>
          <span className="badge badge-neutral">
            Attendees: {attendeeCount}
          </span>
          <span className="badge badge-neutral">
            Volunteers / Leaders: {vlCount}
          </span>
          {survey_type && (
            <span className="badge badge-pending">
              Filter: {survey_type === 'attendee' ? 'Attendee only' : 'Volunteer / Leader only'}
            </span>
          )}
        </div>
      )}

      <div className="grid grid-4" style={{ marginBottom: '1rem' }}>
        <div className="analytics-stat">
          <div className="stat-value">{total}</div>
          <div className="stat-label">Total Responses</div>
        </div>
        <div className="analytics-stat">
          <div className={`stat-value ${ratingTextClass(avg_overall_experience)}`}>
            {avg_overall_experience > 0 ? avg_overall_experience.toFixed(2) : '\u2014'}
            <span className="stat-scale"> / 5</span>
          </div>
          <div className="stat-label">Avg Overall Experience</div>
        </div>
        <div className="analytics-stat">
          <div className={`stat-value ${ratingTextClass(avg_likelihood_to_attend_2026)}`}>
            {avg_likelihood_to_attend_2026 > 0 ? avg_likelihood_to_attend_2026.toFixed(2) : '\u2014'}
            <span className="stat-scale"> / 5</span>
          </div>
          <div className="stat-label">Likely to Attend 2026</div>
        </div>
        <div className="analytics-stat">
          <div className="stat-value">{roles.length}</div>
          <div className="stat-label">Distinct Roles</div>
        </div>
      </div>

      {total > 0 && (
        <>
          <div className="impact-section">
            <h4>Role Breakdown</h4>
            <CountBars entries={roles} total={total} />
          </div>

          <div className="impact-section">
            <h4>Age Group</h4>
            <CountBars entries={ages} total={total} />
          </div>

          <div className="impact-section">
            <h4>Met Expectations</h4>
            <CountBars entries={expectations} total={total} />
          </div>

          <div className="impact-section">
            <h4>Most Impactful Sessions</h4>
            <CountBars entries={top_sessions || []} total={total} />
          </div>

          <div className="impact-section">
            <h4>How Heard About IMPACT</h4>
            <CountBars entries={how_heard_breakdown || []} total={total} />
          </div>

          <div className="impact-section">
            <h4>Average Ratings (1–5)</h4>
            <RatingBars ratings={avg_ratings} questions={questions} />
          </div>

          <div className="impact-section">
            <h4>Call Outcomes</h4>
            <div className="impact-breakdown-badges">
              {Object.entries(outcome_breakdown || {}).map(([outcome, count]) => (
                <span key={outcome} className="badge badge-neutral">
                  {outcome.replace('_', ' ')}: {count}
                </span>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
